import { Accordion, AccordionDetails, Grid, Typography } from "@mui/material";
import AccordionSummarySlim from "components/Common/AccordionSummarySlim";
import CheckBoxForm from "components/Common/CheckBoxForm";
import SelectForm from "components/Common/SelectForm";
import TextFieldForm from "components/Common/TextFieldForm";
import Constants from "helpers/constants";
import { LoginAuthenticationTypeEnum } from "helpers/enums";
import { useTranslations } from "@fenetech/translations";
import { IMfgSite } from "helpers/interfaces";
import { GetEnumAsListOfKeyValuePairs } from "helpers/objects";
import React, { useMemo } from 'react';
import { useFormContext } from "react-hook-form";
import UserStatusChips from "../UserStatusChips";
import MfgSiteSelect from "./MfgSiteSelect";
import AssignableWebCustomers from "./AssignableWebCustomers";
import { IAssignableWebCustomer } from "models/IUser";

interface IProps {
    mfgSites: IMfgSite[] | null,
    assignableWebCustomers: IAssignableWebCustomer[] | null,
    noAssignedUsersWarning: boolean,
    setNoAssignedUsersWarning: (value: boolean) => void,
    handleMfgSitesChange: (isAllSites: boolean, assignedSiteIDs: string[]) => Promise<void>,
    mfgSitesVisible: boolean,
    isEditMode: boolean,
    isAllSites: boolean,
    allSitesVisible: boolean,
    loginAuthenticationVisible: boolean,
    userNameRequired: boolean,
    passwordRequired: boolean,
    firstNameDisabled: boolean,
    lastNameDisabled: boolean,
    emailDisabled: boolean,
    inactiveDisabled: boolean,
    customerInactiveVisible: boolean,
    mfgInactiveVisible: boolean,
    mfgCreatedVisible: boolean,
    mfgCreatedText: string
}

const UserGeneral: React.FC<IProps> = (props: IProps) => {

    const tm = useTranslations();
    const [isExpanded, setIsEpanded] = React.useState<boolean>(true);
    const formMethods = useFormContext();
    const { watch } = formMethods;

    const isDisplayed = (property: boolean) => property ? "flex" : "none";
    const isInactive: boolean = watch('inactive');
    const isCustomerInactive: boolean = watch('customerInactive');
    const isMfgInactive: boolean = watch('mfgInactive');
    const isMfgCreatedEmployee: boolean = watch('mfgCreatedEmployee');
    const loginAuthenticationType = watch("authenticationType");

    const showInactive = useMemo(() => {
        return !props.isEditMode && isInactive;
    }, [props.isEditMode, isInactive]);

    const allSitesVisible = props.isEditMode && props.allSitesVisible && props.mfgSitesVisible;

    const showCustomerInactive = useMemo(() => {
        return props.customerInactiveVisible && isCustomerInactive;
    }, [props.customerInactiveVisible, isCustomerInactive]);

    const showMfgInactive = useMemo(() => {
        return props.mfgInactiveVisible && isMfgInactive;
    }, [props.mfgInactiveVisible, isMfgInactive]);

    const showMfgCreated = useMemo(() => {
        return props.mfgCreatedVisible && isMfgCreatedEmployee;
    }, [props.mfgCreatedVisible, isMfgCreatedEmployee]);

    const showStatusChips = useMemo(() => {
        return showInactive || showCustomerInactive || showMfgInactive || showMfgCreated;
    }, [showInactive, showCustomerInactive, showMfgInactive, showMfgCreated]);

    React.useEffect(() => {
        if (loginAuthenticationType === LoginAuthenticationTypeEnum.SingleSignOn)
        {
            formMethods.setValue("password", "");
            formMethods.setValue("firstName", "");
            formMethods.setValue("lastName", "");
            formMethods.setValue("emailAddress", "");
        }
    }, [loginAuthenticationType, formMethods])

    return <>
        <Accordion expanded={isExpanded} onChange={() => setIsEpanded((prev) => !prev)} disableGutters >
            <AccordionSummarySlim>
                <Typography>
                    {tm.Get("General")}
                </Typography>
            </AccordionSummarySlim>
            <AccordionDetails>
                <Grid container spacing={2} padding={0} paddingTop={allSitesVisible ? 0 : 1}>
                    <Grid item xs={12} sm={6}>
                        <TextFieldForm
                            fieldName="userName"
                            readOnly={!props.isEditMode}
                            variant="standard"
                            id="user.userName"
                            inputProps={{ maxLength: Constants.MaxLength.UserName }}
                            label={tm.Get("User Name")}
                            placeholder={tm.Get("User Name")}
                            disabled={!props.userNameRequired && props.isEditMode}
                            requiredRule={{ value: props.userNameRequired && props.isEditMode, message: tm.GetWithParams("{0} Required", tm.Get("Username")) }}
                            validateValue={{
                                isNotEmpty: (value: string) => new RegExp("^(?!\\s*$).+").test(value) || tm.GetWithParams("{0} cannot be blank.", tm.Get("Username"))
                            }}
                            fullWidth
                        />
                    </Grid>
                    <Grid item sx={{ display: isDisplayed(props.passwordRequired && props.isEditMode) }} xs={12} sm={6}>
                        <TextFieldForm
                            fieldName="password"
                            readOnly={false}
                            type="password"
                            variant="standard"
                            id="user.password"
                            inputProps={{ maxLength: Constants.MaxLength.Password }}
                            label={tm.Get("Password")}
                            placeholder={tm.Get("Password")}
                            requiredRule={{ value: (props.passwordRequired && props.isEditMode), message: tm.GetWithParams("{0} Required", tm.Get("Password")) }}
                            fullWidth
                        />
                    </Grid>
                    {showStatusChips &&
                        <Grid item container xs={12} sm={6} sx={{justifyContent: { sm: "flex-end" }}}>
                            <UserStatusChips
                                isInactive={showInactive}
                                isCustomerInactive={showCustomerInactive}
                                isMfgInactive={showMfgInactive}
                                isMfgCreatedEmployee={showMfgCreated}
                                mfgCreatedText={props.mfgCreatedText}
                            />
                        </Grid>
                    }
                    <Grid item sx={{ display: isDisplayed(props.isEditMode) }} xs={12}>
                        <CheckBoxForm
                            fieldName="inactive"
                            label={tm.Get("User Inactive")}
                            id="user.inactive"
                            readOnly={!props.isEditMode}
                            disabled={props.inactiveDisabled && props.isEditMode}
                        />
                    </Grid>
                    <Grid container item xs={12} spacing={2} padding={0}>
                        <Grid item xs={12} sm={6}>
                            <TextFieldForm
                                fieldName="firstName"
                                readOnly={!props.isEditMode}
                                variant="standard"
                                id="user.firstName"
                                inputProps={{ maxLength: Constants.MaxLength.FirstName }}
                                label={tm.Get("First Name")}
                                placeholder={tm.Get("First Name")}
                                disabled={props.firstNameDisabled && props.isEditMode}
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextFieldForm
                                fieldName="lastName"
                                readOnly={!props.isEditMode}
                                variant="standard"
                                id="user.lastName"
                                inputProps={{ maxLength: Constants.MaxLength.LastName }}
                                label={tm.Get("Last Name")}
                                placeholder={tm.Get("Last Name")}
                                disabled={props.lastNameDisabled && props.isEditMode}
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextFieldForm
                                fieldName="emailAddress"
                                readOnly={!props.isEditMode}
                                variant="standard"
                                id="user.emailAddress"
                                inputProps={{ maxLength: Constants.MaxLength.EmailAddress }}
                                label={tm.Get("Email Address")}
                                placeholder={tm.Get("Email Address")}
                                disabled={props.emailDisabled && props.isEditMode}
                                fullWidth
                            />
                        </Grid>
                    </Grid>
                    <MfgSiteSelect
                        mfgSites={props.mfgSites}
                        requeryAssignableCustomers={props.handleMfgSitesChange}
                        allSitesVisible={allSitesVisible}
                        isVisible={props.mfgSitesVisible}
                        readOnly={!props.isEditMode}
                    />
                    <AssignableWebCustomers
                        assignableWebCustomers={props.assignableWebCustomers}
                        noAssignedUsersWarning={props.noAssignedUsersWarning}
                        setNoAssignedUsersWarning={props.setNoAssignedUsersWarning}
                        isNewUser={props.userNameRequired}
                        readOnly={!props.isEditMode}
                    />
                    <Grid container item xs={12} spacing={2} padding={0}>
                        <Grid item xs={12} sm={6} sx={{ display: isDisplayed(props.loginAuthenticationVisible) }}>
                            <SelectForm
                                fieldName="authenticationType"
                                dataSource={GetEnumAsListOfKeyValuePairs(Object.values(LoginAuthenticationTypeEnum))}
                                keyField="key"
                                valueField="value"
                                displayField="key"
                                translateDisplayField={true}
                                label={tm.Get("Authentication Type")}
                                readOnly={!props.isEditMode}
                            />
                        </Grid>
                    </Grid>
                </Grid>
            </AccordionDetails>
        </Accordion>
    </>;
}

export default UserGeneral;